import { useEffect, useState } from 'react';

// styles
import styles from '../detail.module.scss';

// types
import type { AddressItem } from '../../Sidebar/Sidebar.types';

// components
import { InnerHtml } from 'components/InnerHtml/inner-html';
import { Translation } from 'components/Translation/Translation';
import { ExternalLink } from 'components/Link/ExternalLink';

// utils
import { externalLinkProps } from '../../constants';
import { decodingContent } from 'utils/decodingContent';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { isEmpty } from 'utils/is-empty';

type Model = { product: string; amount: number };

interface HotelDetailProps {
  item: AddressItem;
  detailLayer?: string;
  openAcc?: boolean;
  isAcc?: boolean;
}

export function HotelDetail({
  item: { name, address, fax, phone, email, website, zip_location: zipLocation, models, country },
  detailLayer,
  openAcc = false,
  isAcc = false,
}: Readonly<HotelDetailProps>) {
  const openClass = openAcc ? styles.open : styles.closed;
  const [filteredModels, setFilteredModels] = useState<{ product: string; amount: number }[]>([]);
  const translate = useTranslationFunction();

  useEffect(() => {
    const currentFilteredModels = models?.reduce((acc: Model[], cur) => {
      if (cur.product && cur.amount) {
        const hasModel = acc.some((item) => item.product === cur.product);
        return !hasModel ? [...acc, cur] : acc;
      }

      return acc;
    }, []);

    setFilteredModels(currentFilteredModels ?? []);
  }, [models]);

  const setWrapper = (children) => {
    if (isAcc) {
      return (
        <div className={`${openClass}`}>
          <div className={styles.sidebarAccWrapper}>{children}</div>
        </div>
      );
    }

    return children;
  };

  return setWrapper(
    <>
      <div className={styles.locatorDetail}>
        <ul>
          {!isEmpty(detailLayer) && !isEmpty(name) && (
            <li>
              <InnerHtml as="b" content={name} />
            </li>
          )}
          {!isEmpty(address) && !isAcc && <InnerHtml as="li" content={address} />}
          {!isEmpty(zipLocation) && !isAcc && (
            <li>{!isEmpty(zipLocation) && <InnerHtml as="span" content={zipLocation} />}</li>
          )}
          {!isEmpty(country) && !isAcc && (
            <li>{!isEmpty(country) && <InnerHtml as="span" content={country} />}</li>
          )}
          {!isEmpty(phone) && (
            <li>
              <Translation id="web20_locator_phone" />: {decodingContent(phone)}
            </li>
          )}
          {!isEmpty(fax) && (
            <li>
              <Translation id="web20_locator_fax" />: {decodingContent(fax)}
            </li>
          )}
          {!isEmpty(email) && (
            <li>
              <ExternalLink
                content={{
                  ...externalLinkProps,
                  target: `mailto:${email}`,
                  text: translate('web20_locator_email'),
                  showArrow: true,
                }}
                pure
              />
            </li>
          )}
          {!isEmpty(website) && (
            <li>
              <ExternalLink
                content={{
                  ...externalLinkProps,
                  target: website,
                  text: translate('web20_locator_website'),
                  showArrow: true,
                }}
                pure
              />
            </li>
          )}
        </ul>
      </div>
      {!isEmpty(models) && (
        <div className={styles.modelTable}>
          <table>
            <thead>
              <tr>
                <th>
                  <Translation id="web20_hotel_locator_model" />
                </th>
                <th>
                  <Translation id="web20_hotel_locator_amount" />
                </th>
              </tr>
            </thead>
            <tbody>
              {!isEmpty(filteredModels) &&
                filteredModels.map((model) => (
                  <tr key={`${model.product}-${model.product}`}>
                    <td>{model.product}</td>
                    <td>{model.amount}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </>,
  );
}
